import { ScheduleFilterService } from 'external-services/schedule-filter-service/scheduleFilter.service';
import ObjectCopyService from 'external-services/object-copy-service/index';
import DateUtilsService from 'external-services/date-utils-service';
import {
  ICustomDuration,
  IDiarySchedule,
  IDiaryScheduleRawTimes,
  IDiaryService,
  IEditScheduleService,
  IPouchRows,
  IScheduleExtensionAddOns,
  IScheduleLookup,
} from 'shared-types/index';
import PouchLoadService from 'external-services/pouch-load-service/index';
import {cloneDeep} from "lodash-es";

class ScheduleLookupService {
  private scheduleService: any;
  private scheduleExtensionAddOns: IScheduleExtensionAddOns;
  private allSchedules: IDiaryScheduleRawTimes[] | null = null;
  private track: (name: string, values: [string, any][], customTags?: [string, any][]) => void;

  public setup(
    scheduleService: any,
    scheduleExtensionAddOns: IScheduleExtensionAddOns,
    track?: (name: string, values: [string, any][], customTags: [string, any][]) => void
  ) {
    this.scheduleService = scheduleService;
    this.scheduleExtensionAddOns = scheduleExtensionAddOns;
    if (track) this.track = track;
  }

  public clearCache() {
    this.allSchedules = null;
  }

  /**
   * This function is for unit tests only
   */
  public __setSchedulesCached(schedules: IDiaryScheduleRawTimes[] | null) {
    return (this.allSchedules = schedules);
  }

  public async getSchedules(useLocalState: boolean = true): Promise<IDiaryScheduleRawTimes[]> {
    if (useLocalState && this.allSchedules) {
      return this.allSchedules;
    }

    // just for unit-test to make sure the callback is called properly
    this.callbackCalled();

    const schedulesDocRes: IPouchRows<IDiaryScheduleRawTimes> =
      (await PouchLoadService.getDbLocal().allDocs({
        include_docs: true,
        attachments: false,
        startkey: 'schedule_day_',
        endkey: 'schedule_day_\uffff',
      })) as unknown as IPouchRows<IDiaryScheduleRawTimes>;

    const schedules = schedulesDocRes.rows.map(({ doc }) => {
      return doc;
    });

    // Cache this data if useLocalState is used
    if (useLocalState) {
      this.allSchedules = schedules;
    }

    return schedules;
  }

  public callbackCalled() {
    return 'Called';
  }

  /**
   * Given a date will find a schedule for that day
   * @param {Date} date
   */
  public async getSchedule(
    date: Date,
    scheduleLookupData?: IScheduleLookup,
    useLocalState: boolean = true
  ): Promise<IDiarySchedule> {
    if (!date) {
      return null;
    }

    const dateId: string = DateUtilsService.dateToIdStr(date);
    const scheduleLookUp = scheduleLookupData || this.scheduleService.getScheduleLookUp();

    if (scheduleLookUp[dateId]) {
      // return cached schedule. Cloned to avoid any mutation.
      return cloneDeep(scheduleLookUp[dateId].schedule);
    }

    const dayOfTheWeek = date.getDay();
    const t0 = performance.now();
    const getSchedulesCallback = () => this.getSchedules(useLocalState);
    const schedules: IDiaryScheduleRawTimes[] =
      await ScheduleFilterService.getSchedulesByDayOfTheWeek(dayOfTheWeek, getSchedulesCallback);
    const t1 = performance.now();
    this.track && this.track('getSchedule-getSchedulesByDayOfTheWeek', [['duration', t1 - t0]]);
    // Check if the schedules are valid
    let selectSchedule: IDiaryScheduleRawTimes;
    let lastValidDate: Date;
    schedules.forEach((schedule) => {
      const idParts = schedule._id.split('_');
      const dateStr = idParts[idParts.length - 1];
      const validFrom: Date = DateUtilsService.dateStrToDate(dateStr, '-');
      if (date.getTime() >= validFrom.getTime()) {
        if (!lastValidDate || validFrom.getTime() > lastValidDate.getTime()) {
          selectSchedule = ObjectCopyService.angularSafeCopy(schedule);
          lastValidDate = validFrom;
        }
      }
    });
    if (!selectSchedule) {
      throw new Error('no valid schedule found');
    }

    const resolvedSchedule: IDiarySchedule = await this.addExtrasToSchedule(
      selectSchedule as IDiaryScheduleRawTimes,
      date,
      t0
    );

    if (useLocalState) {
      this.scheduleService.addToCache(dateId, resolvedSchedule);
    } else {
      scheduleLookUp[dateId] = {
        schedule: resolvedSchedule,
        dateAdded: new Date(),
      };
    }
    return resolvedSchedule;
  }

  public async addExtrasToSchedule(
    oldSelectSchedule: IDiaryScheduleRawTimes,
    date: Date,
    t0?: number,
    timezoneId?: string
  ): Promise<IDiarySchedule> {
    const selectSchedule: IDiarySchedule = oldSelectSchedule as unknown as IDiarySchedule;

    let curTime: number = t0;
    const trackDuration = (prevTime: number, msg: string) => {
      curTime = performance.now();
      this.track && this.track(msg, [['duration', curTime - prevTime]]);
    };

    if (curTime) trackDuration(curTime, 'getSchedule-getSchedulesByDayOfTheWeek');

    // used so that Diary can still keep legacy AngularJS logic, while Diary Mobile can use rewritten TS logic
    const _convertServiceTimesToDates = this.scheduleExtensionAddOns?.convertServiceTimesToDates;
    selectSchedule.services = _convertServiceTimesToDates
      ? _convertServiceTimesToDates(
          oldSelectSchedule.services as IEditScheduleService[],
          timezoneId,
          date
        )
      : this.scheduleService.orderServices(oldSelectSchedule.services, date);

    if (curTime) trackDuration(curTime, 'getSchedule-SelectSchedule');

    // used so that Diary can still keep legacy AngularJS logic, while Diary Mobile can use rewritten TS logic
    const _addBlockouts = this.scheduleExtensionAddOns?.addBlockouts;
    const scheduleAddedBlockOuts: IDiarySchedule = _addBlockouts
      ? await _addBlockouts(selectSchedule as IDiarySchedule, date)
      : await this.scheduleService.addBlockouts(selectSchedule, date);

    if (curTime) trackDuration(curTime, 'getSchedule-addBlockouts');

    // used so that Diary can still keep legacy AngularJS logic, while Diary Mobile can use rewritten TS logic
    const _addEvents = this.scheduleExtensionAddOns?.addEvents;
    const scheduleAddedEvents: IDiarySchedule = _addEvents
      ? await _addEvents(scheduleAddedBlockOuts, date)
      : await this.scheduleService.addEvents(scheduleAddedBlockOuts, date);

    if (curTime) trackDuration(curTime, 'getSchedule-addEvents');

    // used so that Diary can still keep legacy AngularJS logic, while Diary Mobile can use rewritten TS logic
    const _addServicesSections = this.scheduleExtensionAddOns?.addServicesSections;
    const resolvedSchedule: IDiarySchedule = _addServicesSections
      ? await _addServicesSections(scheduleAddedEvents, date)
      : await this.scheduleService.addServicesSections(scheduleAddedEvents, date);

    return resolvedSchedule;
  }

  public getDefaultDuration(service: IDiaryService, people: number): number {
    if (!people || !service.customDurations || !service.customDurations.length) {
      return service.bookingDuration;
    }

    let result;
    service.customDurations.some((item: ICustomDuration) => {
      if (people >= item.minPeople && people <= item.maxPeople) {
        result = item.bookingDuration;
        return true;
      }
      return false;
    });
    return result ?? service.bookingDuration;
  }
}
const instance = new ScheduleLookupService();
export default instance as ScheduleLookupService;
